import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useQuery } from '@apollo/client';
import { ACCESS_STATE } from '../../api/queries';
import { onAccessPaused } from '../../config/accessPaused';

// Wraps the signed-in customer dashboard (2026-11-03, Nik):
//  * an always-visible bar — this is the old app, it will be retired, open the new one;
//  * a failed-payment bar while the account is in grace;
//  * a full-screen "access is paused" screen when the server says the account is
//    paused (failed payment past 14 days, cancelled, never paid, trial ended). The
//    server refuses a paused account's data, so without this every screen would
//    fail with raw errors. Paying happens in the new app — it handles every locked
//    state, including the unpaid days this app's payment form can't send.
// The first ACCESS_PAUSED response flips it at once; the 5-minute poll is the backup.
// Staff and an admin signed in as the customer are never shown the paused screen
// (the server exempts impersonation; the customer's own state would read paused).

const NEW_APP = 'https://app.sparkapt.com/';
const NEW_APP_BILLING = 'https://app.sparkapt.com/settings/billing';
const POLL_MS = 5 * 60 * 1000;

class ChildBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }
  static getDerivedStateFromError() {
    return { failed: true };
  }
  componentDidCatch(error) {
    // Keep the paused screen and the bars alive even if a page throws on refused data.
    console.error('[AccessGuard] page failed:', error);
  }
  render() {
    return this.state.failed ? null : this.props.children;
  }
}

const barBase = {
  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10, flexWrap: 'nowrap',
  padding: '6px 16px', fontSize: 13, lineHeight: '18px', color: '#fff', textAlign: 'center',
};
const barLink = {
  color: '#fff', fontWeight: 600, textDecoration: 'none', border: '1px solid rgba(255,255,255,.6)',
  borderRadius: 999, padding: '2px 12px', whiteSpace: 'nowrap',
};

export default function AccessGuard({ children }) {
  const userType = localStorage.getItem('user_type');
  const staff = userType === 'va' || userType === 'admin';
  const impersonating = !!localStorage.getItem('original_localStorage');
  const skip = staff || impersonating;

  const { data, refetch } = useQuery(ACCESS_STATE, {
    skip, pollInterval: POLL_MS, fetchPolicy: 'network-only', errorPolicy: 'all',
  });
  const access = data && data.myProfile ? data.myProfile.subscriptionAccess : null;
  const [refused, setRefused] = useState(false);

  useEffect(() => {
    if (skip) return undefined;
    return onAccessPaused(() => {
      setRefused(true);
      refetch().catch(() => {});
    });
  }, [skip, refetch]);
  // Paid in another tab → the next answer says so; drop the screen.
  useEffect(() => {
    if (access === 'active' || access === 'grace') setRefused(false);
  }, [access]);

  const paused = !skip && (access === 'gated' || refused);
  const grace = !skip && access === 'grace';

  // The dashboard's header is fixed to the top; push it (and the page) down by
  // the bars' height so nothing hides underneath.
  const barsRef = useRef(null);
  useLayoutEffect(() => {
    const root = document.documentElement;
    const el = barsRef.current;
    if (!el) return undefined;
    const apply = () => root.style.setProperty('--legacy-bars-h', `${el.offsetHeight}px`);
    apply();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(apply) : null;
    if (ro) ro.observe(el);
    root.classList.add('legacy-bars-on');
    return () => {
      if (ro) ro.disconnect();
      root.classList.remove('legacy-bars-on');
      root.style.removeProperty('--legacy-bars-h');
    };
  }, []);

  return (
    <>
      <style>{`
        html.legacy-bars-on body { padding-top: var(--legacy-bars-h, 0px); }
        /* margin, not top: some pinned bars set their own top (the filter bar and
           right sidebar use top: 65px), and a margin shifts them whatever it is. */
        html.legacy-bars-on .fixed-top,
        html.legacy-bars-on .sidebar-right { margin-top: var(--legacy-bars-h, 0px); }
        .legacy-bar-short { display: none; }
        @media (max-width: 575px) {
          .legacy-bar-long { display: none; }
          .legacy-bar-short { display: inline; }
        }
      `}</style>
      <div ref={barsRef} style={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 1031 }}>
        {grace && (
          <div role='status' style={{ ...barBase, background: '#b45309' }}>
            <span className='legacy-bar-long'>Your payment didn&rsquo;t go through. Update your card in the new SparkAPT app to keep access.</span>
            <span className='legacy-bar-short'>Your payment didn&rsquo;t go through.</span>
            <a href={NEW_APP_BILLING} target='_blank' rel='noopener noreferrer' style={barLink}>Update card</a>
          </div>
        )}
        <div style={{ ...barBase, background: '#111827' }}>
          <span className='legacy-bar-long'>You&rsquo;re using the old SparkAPT app. We&rsquo;ll retire it soon.</span>
          <span className='legacy-bar-short'>Old SparkAPT app, retiring soon.</span>
          <a href={NEW_APP} target='_blank' rel='noopener noreferrer' style={barLink}>
            <span className='legacy-bar-long'>Open the new app</span>
            <span className='legacy-bar-short'>Open new app</span>
          </a>
        </div>
      </div>

      <ChildBoundary>{children}</ChildBoundary>

      {paused && (
        <div
          role='dialog'
          aria-modal='true'
          aria-labelledby='legacy-paused-title'
          style={{
            position: 'fixed', inset: 0, zIndex: 2147483000, background: 'rgba(17,24,39,.6)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
          }}
        >
          <div style={{ background: '#fff', borderRadius: 16, maxWidth: 440, width: '100%', padding: 28, boxShadow: '0 20px 50px rgba(0,0,0,.25)' }}>
            <h2 id='legacy-paused-title' style={{ fontSize: 20, fontWeight: 700, margin: '0 0 8px', color: '#111827' }}>
              Your SparkAPT access is paused
            </h2>
            <p style={{ fontSize: 15, color: '#4b5563', margin: '0 0 20px', lineHeight: 1.5 }}>
              To keep using SparkAPT, pay in the new SparkAPT app. Your clients, lists and guest cards are saved.
            </p>
            <a
              href={NEW_APP}
              target='_blank'
              rel='noopener noreferrer'
              style={{
                display: 'block', textAlign: 'center', background: '#215afe', color: '#fff', borderRadius: 999,
                padding: '10px 16px', fontWeight: 600, textDecoration: 'none', marginBottom: 10,
              }}
            >
              Pay in the new app
            </a>
            <a
              href='/logout'
              style={{
                display: 'block', textAlign: 'center', border: '1px solid #d1d5db', color: '#374151', borderRadius: 999,
                padding: '10px 16px', fontWeight: 500, textDecoration: 'none',
              }}
            >
              Sign out
            </a>
            <p style={{ fontSize: 12, color: '#6b7280', textAlign: 'center', margin: '16px 0 0' }}>
              Questions about billing? Email <a href='mailto:data@sparkapt.com'>data@sparkapt.com</a>
            </p>
          </div>
        </div>
      )}
    </>
  );
}
